import { useCallback, useEffect, useRef, useState } from 'react';
import { notification, Popconfirm } from 'antd';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/pro-light-svg-icons/faCamera';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons/faTrashAlt';
import { useDispatch } from 'src/store/use-dispatch';
import { getLang } from 'shared/store/selectors/lang.selector';
import { updateAvatar, deleteAvatar } from 'shared/store/actions/users-management.actions';
import type { User } from 'shared/types/user';
import { useAccessiblePopconfirm } from 'src/components/design-system/popconfirm/hooks/use-accessible-popconfirm';
import { profilePhotoReporter } from 'src/helpers/profile-photo-reporter';
import { CropModal } from './crop-modal.component';
import { MAX_AVATAR_BYTES } from './avatar.constants';
import styles from './avatar-edit-overlay.module.scss';

interface AvatarEditOverlayProps {
  user: User;
}

export function AvatarEditOverlay({ user }: AvatarEditOverlayProps) {
  const dispatch = useDispatch();
  const lang = useSelector(getLang('PROFILE_PHOTO'));
  const generalLang = useSelector(getLang('GENERAL'));
  const [api, contextHolder] = notification.useNotification();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // While a crop session is active: revoke the object URL when it ends.
  // When no active session: clear the file input so re-picking the same file
  // triggers `change` again (HTML file inputs swallow same-value re-picks).
  useEffect(() => {
    if (cropSrc) {
      return () => URL.revokeObjectURL(cropSrc);
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [cropSrc]);

  const openPicker = useCallback(() => {
    profilePhotoReporter.uploadPhotoClick({ source: 'file', targetUserId: user.id });
    inputRef.current?.click();
  }, [user.id]);

  const onFileChosen = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCropSrc(URL.createObjectURL(file));
  }, []);

  const onCropConfirm = useCallback(
    async (blob: Blob) => {
      setCropSrc(null);
      if (blob.size > MAX_AVATAR_BYTES) {
        api.error({ message: lang.UPLOAD_TOO_LARGE, placement: 'bottomRight' });
        return;
      }
      setBusy(true);
      try {
        const file = new File([blob], 'avatar.png', { type: 'image/png' });
        const { data, error } = await dispatch(updateAvatar({ userId: user.id, file }));
        if (error) throw error;
        profilePhotoReporter.uploadPhoto({
          source: 'file',
          newPhotoId: data?.user?.avatarPublicId,
          targetUserId: user.id,
        });
        api.success({ message: lang.UPLOAD_SUCCESS, placement: 'bottomRight' });
      } catch (error) {
        // `updateAvatar` resolves backend rejections to the upstream message
        // string (e.g. assets-service's "Unsupported image type").
        api.error({
          message: lang.UPLOAD_ERROR,
          description: typeof error === 'string' ? error : undefined,
          placement: 'bottomRight',
        });
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `api` (notification.useNotification) isn't referentially stable
    [dispatch, user.id, lang],
  );

  const onDelete = useCallback(async () => {
    setBusy(true);
    const removedPhotoId = user.avatarPublicId;
    try {
      const { error } = await dispatch(deleteAvatar({ userId: user.id }));
      if (error) throw error;
      profilePhotoReporter.removePhotoSubmit({ photoId: removedPhotoId, targetUserId: user.id });
      api.success({ message: lang.REMOVE_SUCCESS, placement: 'bottomRight' });
    } catch {
      api.error({ message: lang.REMOVE_ERROR, placement: 'bottomRight' });
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `api` (notification.useNotification) isn't referentially stable
  }, [dispatch, user.id, user.avatarPublicId, lang]);

  const removePopconfirmProps = useAccessiblePopconfirm({ onConfirm: onDelete });

  const hasAvatar = !!user.avatarPublicId;

  return (
    <>
      {contextHolder}
      <div
        data-show-on-hover
        className={`${styles.overlay} ${hasAvatar ? styles.overlayDelete : styles.overlayUpload}`}>
        {hasAvatar ? (
          <Popconfirm
            {...removePopconfirmProps}
            title={lang.REMOVE_CONFIRM_TITLE}
            description={lang.REMOVE_CONFIRM_BODY}
            okText={lang.REMOVE_CONFIRM_OK}
            cancelText={generalLang.CANCEL}>
            <button
              type="button"
              className={styles.iconBtn}
              disabled={busy}
              onClick={() =>
                profilePhotoReporter.removePhotoClick({
                  photoId: user.avatarPublicId,
                  targetUserId: user.id,
                })
              }
              aria-label={lang.REMOVE_BUTTON_LABEL}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
          </Popconfirm>
        ) : (
          <button
            type="button"
            className={styles.iconBtn}
            onClick={openPicker}
            disabled={busy}
            aria-label={lang.UPLOAD_BUTTON_LABEL}>
            <FontAwesomeIcon icon={faCamera} />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={onFileChosen}
        multiple={false}
      />
      <CropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={onCropConfirm}
      />
    </>
  );
}
